import type { ChartDataRow, ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';

export type ChartPreviewDataRow = Record<string, string | number>;

export type ChartPreviewRequest = Omit<ChartRequest, 'data' | 'categoryField' | 'valueFields'> & {
  categoryField: string;
  valueFields: string[];
  data: ChartPreviewDataRow[];
};

type CategoryAxisLabelLayout = {
  rotateLabels: boolean;
  minGridDistance: number;
  maxLabelWidth: number;
};

type CategoryAxisLabelLayoutParams = {
  data: ChartPreviewDataRow[];
  categoryField: string;
  containerWidth: number;
  maxLabelWidth?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const normalizeChartRequest = (chart: ChartRequest): ChartPreviewRequest => {
  const categoryField = chart.categoryField || 'category';
  const rawData = (Array.isArray(chart.data) ? chart.data : []) as ChartDataRow[];
  const valueFields = Array.isArray(chart.valueFields) ? chart.valueFields : [];

  const data = rawData
    .map((item) => {
      if (!isRecord(item)) return null;

      const row: ChartPreviewDataRow = {};
      const categoryValue = item[categoryField];
      if (typeof categoryValue === 'string' || typeof categoryValue === 'number') {
        row[categoryField] = categoryValue;
      }

      for (const field of valueFields) {
        const value = item[field];
        if (typeof value === 'number') {
          row[field] = value;
        }
      }

      // Preserve any extra string/number metadata fields (e.g. isAnomalous) so chart components
      // can use them without re-fetching data.
      for (const [key, value] of Object.entries(item)) {
        if (!(key in row) && (typeof value === 'string' || typeof value === 'number')) {
          row[key] = value;
        }
      }

      return Object.keys(row).length > 0 ? row : null;
    })
    .filter((row): row is ChartPreviewDataRow => row !== null);

  return {
    ...chart,
    categoryField,
    valueFields,
    data,
  };
};

export const getCategoryAxisLabelLayout = (params: CategoryAxisLabelLayoutParams): CategoryAxisLabelLayout => {
  const { data, categoryField, containerWidth, maxLabelWidth = 90 } = params;
  const safeWidth = Number.isFinite(containerWidth) ? containerWidth : 0;
  const labelLengths = data.map((row) => String(row[categoryField] ?? '').length);
  const averageLabelLength =
    labelLengths.length > 0 ? labelLengths.reduce((sum, length) => sum + length, 0) / labelLengths.length : 0;
  const longestLabelLength = labelLengths.length > 0 ? Math.max(...labelLengths) : 0;
  const widthPerLabel = safeWidth > 0 && data.length > 0 ? safeWidth / data.length : 0;
  const estimatedLongestLabelWidth = longestLabelLength * 7;
  const needsRotationForSpace = widthPerLabel > 0 && widthPerLabel < Math.min(70, estimatedLongestLabelWidth + 10);
  const rotateLabels = averageLabelLength > 10 || data.length > 6 || needsRotationForSpace;
  const computedMaxWidth =
    widthPerLabel > 0 ? Math.min(maxLabelWidth, Math.max(24, Math.floor(widthPerLabel - 6))) : maxLabelWidth;

  return {
    rotateLabels,
    minGridDistance: rotateLabels ? 30 : 20,
    maxLabelWidth: computedMaxWidth,
  };
};
